/**
 * Datetime helpers shared across the app.
 *
 * Backend stores naive UTC datetimes (no `Z` suffix) and returns them as
 * ISO-like strings such as `"2026-04-20T10:30:00"`. The browser's `Date`
 * constructor would otherwise interpret those as local time, so all parsing
 * MUST go through `parseUtc`.
 *
 * Pour responses include `TimeZoneIANA` (e.g. `"Australia/Sydney"`) sourced
 * from the joined ProjectInfo.TimeZone Windows ID. Pass that to the format
 * helpers so the user sees the project's local time, not the browser's.
 */

export type IsoLike = string | number | Date | null | undefined;

/**
 * One `Intl.DateTimeFormat` per locale and options, built on first use.
 * Building one is costly (milliseconds each in some browsers) and lists
 * format a date per row on every render. A rejected timezone still throws,
 * as `new Intl.DateTimeFormat` would, and is not cached.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>();
const dateTimeFormat = (
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat => {
  const key = `${locale}|${JSON.stringify(options)}`;
  let dtf = formatterCache.get(key);
  if (!dtf) {
    dtf = new Intl.DateTimeFormat(locale, options);
    formatterCache.set(key, dtf);
  }
  return dtf;
};

const isoEndsWithTzMarker = (s: string): boolean =>
  s.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(s);

/**
 * Parse a backend datetime string as UTC. Also accepts already-numeric
 * timestamps (Highcharts click events) or `Date` instances and returns them
 * as-is.
 *
 * Returns `Invalid Date` for empty/bad input — callers can detect via
 * `isNaN(d.getTime())`.
 */
export function parseUtc(value: IsoLike): Date {
  if (value == null || value === '') return new Date(NaN);
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  // Some responses send "2026-10-17 10:30:00" (strftime): use the ISO "T"
  // separator, which every browser parses.
  const iso = value.replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})/, '$1T$2');
  const normalized = isoEndsWithTzMarker(iso) ? iso : iso + 'Z';
  return new Date(normalized);
}

/**
 * Format a UTC datetime in the given IANA timezone using `Intl.DateTimeFormat`.
 * Falls back to UTC if the timezone is missing or rejected by the browser.
 *
 * Default options match the legacy short-datetime format: `dd/MM/yyyy HH:mm`.
 */
export function formatInTimeZone(
  value: IsoLike,
  ianaTimeZone: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = parseUtc(value);
  if (isNaN(date.getTime())) return '';
  const tz = ianaTimeZone || 'UTC';
  const opts: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: tz,
    ...options,
  };
  try {
    return dateTimeFormat('en-GB', opts).format(date);
  } catch {
    return dateTimeFormat('en-GB', { ...opts, timeZone: 'UTC' }).format(date);
  }
}

/**
 * Labelled short datetime, e.g. `"17 Jun 2026, 13:30"`. Uses a month *name*
 * so the value is unambiguous across locales (avoids the dd/MM vs MM/dd
 * confusion of all-numeric dates). Thin wrapper over `formatInTimeZone`.
 */
export function formatDateTimeLabelled(
  value: IsoLike,
  ianaTimeZone: string | null | undefined,
): string {
  return formatInTimeZone(value, ianaTimeZone, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Short labelled date, e.g. `"20 Jun"` (day + month name, no year/time). Used
 * for compact card date ranges. Month name keeps it locale-unambiguous.
 */
export function formatDateShort(
  value: IsoLike,
  ianaTimeZone: string | null | undefined,
): string {
  return formatInTimeZone(value, ianaTimeZone, {
    day: '2-digit',
    month: 'short',
    year: undefined,
    hour: undefined,
    minute: undefined,
  });
}

/**
 * Short name of the timezone a time is shown in, e.g. `"AEST"` / `"AEDT"`
 * (daylight saving aware for that date), `"ACST"`, `"NZDT"`, or an offset
 * like `"GMT+3"` where there is no well-known abbreviation. `en-AU` gives
 * the familiar Australian/NZ names. Missing timezone → `"UTC"`, which is
 * what the time is then displayed in. Uses "now" when the value is empty.
 */
export function timeZoneLabel(
  value: IsoLike,
  ianaTimeZone: string | null | undefined,
): string {
  const parsed = parseUtc(value);
  const date = isNaN(parsed.getTime()) ? new Date() : parsed;
  const tz = ianaTimeZone || 'UTC';
  try {
    return (
      dateTimeFormat('en-AU', { timeZone: tz, timeZoneName: 'short' })
        .formatToParts(date)
        .find((p) => p.type === 'timeZoneName')?.value ?? tz
    );
  } catch {
    return 'UTC';
  }
}

/**
 * Labelled short datetime followed by the timezone it is shown in, e.g.
 * `"17 Jun 2026, 13:30 AEST"`: `formatDateTimeLabelled` plus `timeZoneLabel`
 * for that instant, so a time near a daylight saving change names the
 * right zone. Empty for empty / invalid input (no stray zone name).
 */
export function formatDateTimeWithZone(
  value: IsoLike,
  ianaTimeZone: string | null | undefined,
): string {
  const text = formatDateTimeLabelled(value, ianaTimeZone);
  return text ? `${text} ${timeZoneLabel(value, ianaTimeZone)}` : '';
}

/**
 * Time of day followed by its timezone, e.g. `"13:30 AEST"`, for compact
 * status text such as the dashboard's refresh notice. Empty for empty /
 * invalid input.
 */
export function formatTimeWithZone(
  value: IsoLike,
  ianaTimeZone: string | null | undefined,
): string {
  const text = formatInTimeZone(value, ianaTimeZone, {
    day: undefined,
    month: undefined,
    year: undefined,
  });
  return text ? `${text} ${timeZoneLabel(value, ianaTimeZone)}` : '';
}

const datetimeLocalParts = (
  date: Date,
  ianaTimeZone: string,
): { year: string; month: string; day: string; hour: string; minute: string } => {
  const dtf = dateTimeFormat('en-US', {
    timeZone: ianaTimeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const parts = dtf.formatToParts(date).reduce<Record<string, string>>((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour === '24' ? '00' : parts.hour,
    minute: parts.minute,
  };
};

/**
 * Format a UTC API value as a `YYYY-MM-DDTHH:mm` string suitable for
 * binding to an `<input type="datetime-local">`. The string represents the
 * wall-clock time in `ianaTimeZone` (the project's TZ), so the user sees
 * times in their project's local timezone regardless of the browser's TZ.
 *
 * Returns `''` for empty / invalid input.
 */
export function utcToLocalInput(
  value: IsoLike,
  ianaTimeZone: string | null | undefined,
): string {
  const date = parseUtc(value);
  if (isNaN(date.getTime())) return '';
  if (!ianaTimeZone) {
    // No TZ info — display raw UTC components so the value is still legible.
    return date.toISOString().slice(0, 16);
  }
  try {
    const p = datetimeLocalParts(date, ianaTimeZone);
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
  } catch {
    return date.toISOString().slice(0, 16);
  }
}

/**
 * Inverse of `utcToLocalInput`. Take a `<input type="datetime-local">` value
 * (interpreted as wall-clock time in `ianaTimeZone`) and return the UTC ISO
 * string the backend expects (no `Z` — naive UTC, matching the existing
 * column convention).
 *
 * Returns `''` for empty input.
 */
export function localInputToUtc(
  localInputValue: string | null | undefined,
  ianaTimeZone: string | null | undefined,
): string {
  if (!localInputValue) return '';
  // Browser interprets naked datetime-local as the browser's local TZ, but we
  // need it interpreted in `ianaTimeZone`. Use the offset trick: form a UTC
  // date from the components, compute the TZ's offset at that instant, and
  // shift by that offset to get the true UTC instant.
  const m = localInputValue.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/,
  );
  if (!m) return '';
  const [, y, mo, d, h, mi, s] = m;
  const utcGuess = new Date(
    Date.UTC(+y, +mo - 1, +d, +h, +mi, s ? +s : 0),
  );
  if (isNaN(utcGuess.getTime())) return '';
  const tz = ianaTimeZone || 'UTC';
  const offsetMinutes = getTimeZoneOffsetMinutes(tz, utcGuess);
  // offsetMinutes follows JS convention: positive when local is *behind* UTC.
  // True UTC instant = guessed-UTC + offset.
  const trueUtc = new Date(utcGuess.getTime() + offsetMinutes * 60_000);
  // Backend stores naive UTC — emit ISO without trailing Z.
  return trueUtc.toISOString().replace(/\.\d{3}Z$/, '');
}

/**
 * Compute the offset (in minutes) of the given IANA timezone at the given
 * instant. Used by chart libraries (e.g. Highcharts) that need a
 * `getTimezoneOffset` callback for UTC→TZ axis labels.
 *
 * Returns `0` if the timezone is unknown.
 */
export function getTimeZoneOffsetMinutes(
  ianaTimeZone: string | null | undefined,
  at: Date = new Date(),
): number {
  if (!ianaTimeZone) return 0;
  try {
    const dtf = dateTimeFormat('en-US', {
      timeZone: ianaTimeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    const parts = dtf.formatToParts(at).reduce<Record<string, string>>((acc, p) => {
      if (p.type !== 'literal') acc[p.type] = p.value;
      return acc;
    }, {});
    const asUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour === '24' ? '0' : parts.hour),
      Number(parts.minute),
      Number(parts.second),
    );
    // Highcharts convention: positive value = west of UTC (negative offset)
    return -Math.round((asUtc - at.getTime()) / 60000);
  } catch {
    return 0;
  }
}
